import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Avatar,
  Form,
  Input,
  Button,
  Spin,
  Tag,
  Card,
  Statistic,
  Row,
  Col
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  BookOutlined
} from "@ant-design/icons";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function AdminDetails() {
  const { id } = useParams();
 
   const navigate = useNavigate();

  // Auth info
  const token = localStorage.getItem("token");
  const fullName = localStorage.getItem("fullName");
  const role = (localStorage.getItem("role") || "").toUpperCase();

  useEffect(() => {
    if (!token || !fullName || !role) {
      navigate("/user/sign_in");
    }
  }, [token, fullName, role, navigate]);
  
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadAdmin = async () => {
      try {
        setLoading(true);

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/user/admin/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data.success) {
          setUser(res.data.data);
          form.setFieldsValue(res.data.data);
        }

      } finally {
        setLoading(false);
      }
    };

    loadAdmin();
  }, [id, token, form]);

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-100">
        <Spin size="large" />
      </div>
    );

  if (!user) return <div className="p-10">Admin not found</div>;

 

const handleUpdateAdmin = async () => {
  try {
    setSaving(true);

    const values = await form.validateFields();

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/user/admin/update`,
      {
        Email: user.Email,
        ...values
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    if (response.data.success) {
      toast.success("Admin updated successfully 🔥");
      setUser(response.data.data);
    } else {
      toast.error(response.data.message);
    }

  } catch (error) {
    toast.error(error?.response?.data?.message || "Update failed ❌");
  } finally {
    setSaving(false);
  }
};

  return (
    <div className="min-h-screen p-10 bg-slate-100">

      <ToastContainer
                    position="bottom-left"
                    autoClose={5000}
                    hideProgressBar={true}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="dark"
                    className="!mb-6 !ml-4"
                    toastClassName="!inline-flex !items-center !whitespace-nowrap !bg-black !text-white !px-4 !py-2 !rounded-lg !w-auto !min-w-0 !shadow-lg"
                  />

      {/* HEADER SECTION */}
      <div className="p-10 mb-10 text-white shadow-xl bg-gradient-to-r from-slate-800 to-indigo-900 rounded-3xl">
        <div className="flex items-center gap-6">
          <Avatar
            size={110}
            src={user.ProfilePhotoUrl}
            icon={<UserOutlined />}
            className="border-4 border-white shadow-lg"
          />
          <div>
            <h1 className="text-3xl font-bold">{user.FullName}</h1>
            <p className="opacity-80">{user.Email}</p>
            <Tag color="red" className="mt-3 text-sm font-semibold">
             {user.Role}
            </Tag>
          </div>
        </div>
      </div>

      {/* STATS ROW */}
      <Row gutter={24} className="mb-10">
        <Col xs={24} md={12}>
          <Card className="border-0 shadow-md rounded-2xl">
            <Statistic
              title="Users Created"
              value={user.UsersCreated || 0}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card className="border-0 shadow-md rounded-2xl">
            <Statistic
              title="Courses Created"
              value={user.CoursesCreated || 0}
              prefix={<BookOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* MAIN CONTENT */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">

        {/* LEFT PROFILE SUMMARY */}
        <div className="p-8 bg-white shadow-lg rounded-3xl lg:col-span-1 h-fit">
          <h3 className="mb-6 text-lg font-semibold text-gray-700">
            Quick Info
          </h3>

          <div className="space-y-4 text-sm text-gray-600">
            <div>
              <span className="font-semibold">Phone:</span>{" "}
              {user.PhoneNumber}
            </div>
            <div>
              <span className="font-semibold">Country:</span>{" "}
              {user.Country}
            </div>
            <div>
              <span className="font-semibold">Role:</span>{" "}
              {user.Role}
            </div>
          </div>
        </div>

        {/* RIGHT FORM PANEL */}
        <div className="p-10 bg-white shadow-lg rounded-3xl lg:col-span-3">
          <h2 className="mb-8 text-2xl font-semibold text-slate-800">
            Admin Account Settings
          </h2>

          <Form form={form} layout="vertical">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

              <Form.Item name="FullName" label="Full Name">
                <Input size="large" />
              </Form.Item>

              <Form.Item name="Email" label="Email">
                <Input size="large" />
              </Form.Item>

              <Form.Item name="PhoneNumber" label="Phone Number">
                <Input size="large" />
              </Form.Item>

              <Form.Item name="Country" label="Country">
                <Input size="large" />
              </Form.Item>

            </div>

            <Button
  type="primary"
  size="large"
  loading={saving}
  onClick={handleUpdateAdmin}
  className="px-12 mt-10 !bg-rose-600 hover:!bg-rose-700 rounded-xl font-semibold"
>
  Update Admin
</Button>
          </Form>
        </div>

      </div>
    </div>
  );
}

export default AdminDetails;